import { tx as translateInterfaceText } from '@/utils/i18n';
/**
 * Headline Overlay Validators
 * Sanitization and validation utilities for headline overlay features
 */

// Allowed HTML tags for sanitization
const ALLOWED_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'b', 'i', 'strong', 'em', 'span', 'br', 'div'];

// Allowed style attributes
const ALLOWED_STYLE_PROPS = ['color', 'font-size', 'font-weight', 'text-align', 'font-family'];

/**
 * Sanitizes HTML input by:
 * - Removing script tags and iframes
 * - Removing event handlers (onclick, onload, etc.)
 * - Allowing only specific tags and style attributes
 */
export const sanitizeHTML = (html: string): string => {
  // Remove script tags and their content
  let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove iframe tags
  sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
  
  // Remove event handlers (onclick, onload, onerror, etc.)
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');
  
  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '');
  
  // Filter style attributes to only allow safe properties
  sanitized = sanitized.replace(/style\s*=\s*["']([^"']*)["']/gi, (match, styles) => {
    const safeStyles = styles
      .split(';')
      .filter((style: string) => {
        const prop = style.split(':')[0].trim().toLowerCase();
        return ALLOWED_STYLE_PROPS.includes(prop);
      })
      .join(';');
    
    return safeStyles ? `style="${safeStyles}"` : '';
  });
  
  return sanitized.trim();
};

/**
 * Validates image file format and size
 */
export interface ImageValidationResult {
  valid: boolean;
  error?: string;
}

export const validateImage = (
  file: File,
  maxSizeMB: number = 2,
  acceptedFormats: string[] = ['image/png', 'image/jpeg']
): ImageValidationResult => {
  // Check format
  if (!acceptedFormats.includes(file.type)) {
    return { 
      valid: false, 
      error: translateInterfaceText("Formato não suportado. Use PNG ou JPG.")
    };
  }
  
  // Check size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return { 
      valid: false, 
      error: `Arquivo maior que ${maxSizeMB}MB` 
    };
  }
  
  return { valid: true };
};

/**
 * Validates timing configuration
 */
export interface TimingValidationResult {
  valid: boolean;
  error?: string;
}

export const validateTiming = (
  start: number,
  end: number
): TimingValidationResult => {
  // End must be 0 (until the end) or >= start
  if (end !== 0 && end < start) {
    return {
      valid: false,
      error: translateInterfaceText("O fim deve ser 0 ou maior/igual ao início.")
    };
  }
  
  // Start must be >= 0
  if (start < 0) {
    return {
      valid: false,
      error: translateInterfaceText("O início deve ser maior ou igual a zero.")
    };
  }
  
  return { valid: true };
};

/**
 * Normalizes decimal input (converts comma to dot)
 */
export const normalizeDecimal = (value: string): number => {
  const normalized = value.replace(',', '.');
  const num = parseFloat(normalized);
  return isNaN(num) ? 0 : num;
};

/**
 * Formats number to display with comma decimal separator
 */
export const formatDecimal = (value: number, decimals: number = 1): string => {
  return value.toFixed(decimals).replace('.', ',');
};
